import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, TemplateRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BaseCellComponent } from '../base-cell.component';
import { DatasetService } from 'src/app/services/dataset.service';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { Dataset, DataTableSchema, DataSchemaDataType } from 'src/app/services/models/datasets.models';
import { ViewTab, ViewManager, ContextObjectConfig } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { DynamicTableFilters, MatchMode, SortCriteria } from 'src/app/services/models/dynamic-table.models';
import { CellFilterService } from 'src/app/common/bhub-common-module/services/cell-filter.service';
import { Subject, takeUntil, debounceTime, switchMap, EMPTY } from 'rxjs';

interface TableColumn {
  fieldId: number;
  fieldName: string;
  displayName: string;
  dataType: string;
}

interface TableData {
  [key: string]: any;
}

interface FilterRow {
  fieldId: number | null;
  fieldName: string;
  value: string;
  value2?: string;
  dateValue?: Date | null;
  dateValue2?: Date | null;
  matchMode: MatchMode;
  dataType: string;
  availableMatchModes?: { label: string; value: MatchMode }[];
  isPreset?: boolean;
}

@Component({
  selector: 'bhub-multi-select-cell',
  standalone: false,
  templateUrl: './multi-select-cell.component.html',
  styleUrl: './multi-select-cell.component.scss'
})
export class MultiSelectCellComponent extends BaseCellComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('tableHeader', { static: false }) tableHeader!: ElementRef<HTMLDivElement>;
  @ViewChild('tableBody', { static: false }) tableBody!: ElementRef<HTMLDivElement>;
  @ViewChild('filterDialog') filterDialog!: TemplateRef<any>;
  @Input() availableTabs: ViewTab[] = [];
  @Input() viewManager: ViewManager | null = null;
  @Output() contextDataChange = new EventEmitter<{ [contextKey: string]: any }>();
  @Output() tabNavigation = new EventEmitter<string>();

  columns: TableColumn[] = [];
  tableData: TableData[] = [];
  filteredData: TableData[] = [];
  displayedColumns: string[] = [];
  isLoadingData = false;

  // Sorting
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | null = null;

  // Filter properties
  filterRows: FilterRow[] = [];
  hasActiveFilters = false;
  filterDisplayMode: 'dialog' | 'inline' = 'dialog';

  // Pagination
  paginationEnabled = false;
  currentPage = 0;
  pageSize = 500;
  totalRecords = 0;

  // Multi-selection tracking
  selectedRowIndices: Set<number> = new Set();
  selectAllChecked = false;

  // Navigation
  get navigationEnabled(): boolean {
    return this.getConfigValue('enableNavigation', false);
  }

  get navigationButtonLabel(): string {
    return this.getConfigValue('navigationButtonLabel', 'Continue') || 'Continue';
  }

  private datasetSchemas: DataTableSchema[] = [];
  private destroy$ = new Subject<void>();
  private loadDataset$ = new Subject<number>();
  private currentDatasetId: number | null = null;
  private inlineFilterApply$ = new Subject<void>();

  constructor(
    private datasetService: DatasetService,
    private dynamicTableService: DynamicTableService,
    private cellFilterService: CellFilterService,
    private dialog: MatDialog
  ) {
    super();

    // Set up debounced inline filter apply
    this.inlineFilterApply$
      .pipe(
        debounceTime(1750),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.applyInlineFilters();
      });

    this.loadDataset$
      .pipe(
        debounceTime(100),
        switchMap(datasetId => {
          if (datasetId === this.currentDatasetId) {
            return EMPTY;
          }
          this.currentDatasetId = datasetId;
          this.isLoadingData = true;
          return this.datasetService.getDatasetReferences();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (datasetsMap: Map<number, Dataset>) => {
          if (this.currentDatasetId) {
            const dataset = datasetsMap.get(this.currentDatasetId);
            if (dataset) {
              this.setupTableColumns(dataset.dataTableSchemas);
              this.initPresetFilters();
              this.restoreFilterState();
              this.loadTableData(this.currentDatasetId);
            } else {
              this.isLoadingData = false;
            }
          }
        },
        error: (error) => {
          console.error('Error loading dataset:', error);
          this.isLoadingData = false;
          this.currentDatasetId = null;
        }
      });
  }

  ngOnDestroy(): void {
    // Only save filter state if the component actually finished loading (has columns).
    // A component that was created and destroyed before loading shouldn't overwrite saved state.
    if (this.columns.length > 0) {
      if (this.cell && this.filterRows.length > 0) {
        this.cell.configuration._activeFilterState = this.filterRows.map(r => ({ ...r }));
        this.cell.configuration._activeFilterHasActive = this.hasActiveFilters;
      } else if (this.cell) {
        delete this.cell.configuration._activeFilterState;
        delete this.cell.configuration._activeFilterHasActive;
      }
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.tableHeader && this.tableBody) {
        const headerElement = this.tableHeader.nativeElement;
        const bodyElement = this.tableBody.nativeElement;

        bodyElement.addEventListener('scroll', () => {
          headerElement.scrollLeft = bodyElement.scrollLeft;
        });

        headerElement.addEventListener('scroll', () => {
          bodyElement.scrollLeft = headerElement.scrollLeft;
        });
      }
    }, 100);
  }

  protected initializeCell(): void {
    this.filterDisplayMode = this.getConfigValue('filterDisplayMode', 'dialog') as 'dialog' | 'inline';
    this.initializeContext();

    if (this.cell?.dataBinding?.datasetId) {
      this.loadDataset$.next(this.cell.dataBinding.datasetId);
    } else {
      this.columns = [];
      this.tableData = [];
      this.filteredData = [];
      this.displayedColumns = [];
      this.currentDatasetId = null;
    }
  }

  private initializeContext(): void {
    if (!this.viewManager) return;

    if (!this.viewManager.contextData) {
      this.viewManager.contextData = {};
    }

    // Initialize primary context (array of selected items)
    const contextVariable = this.getConfigValue('multiSelectContextVariable', '');
    if (contextVariable) {
      if (!this.viewManager.contextData.hasOwnProperty(contextVariable)) {
        this.viewManager.contextData[contextVariable] = [];
      }
    }

    // Initialize additional contexts
    const additionalContexts = this.getConfigValue('additionalContexts', []) as ContextObjectConfig[];
    if (additionalContexts && additionalContexts.length > 0) {
      additionalContexts.forEach(additionalConfig => {
        if (additionalConfig.contextVariable && additionalConfig.contextVariable.trim()) {
          const contextKey = additionalConfig.contextVariable;

          // Initialize context data with empty object
          if (!this.viewManager!.contextData.hasOwnProperty(contextKey)) {
            this.viewManager!.contextData[contextKey] = {
              __metadata: { datasetId: additionalConfig.datasetId, isEmptyContext: true, isNewRecord: false }
            };
          }
        }
      });
    }
  }

  protected onCellOrDataChange(): void {
    this.initializeCell();
  }

  protected onCellUpdate(): void {
    this.initializeCell();
  }

  protected onDataSet(data: any): void {
    // Reload data when external data changes
    if (this.cell?.dataBinding?.datasetId) {
      this.loadTableData(this.cell.dataBinding.datasetId);
    }
  }

  reloadData(): void {
    if (this.cell?.dataBinding?.datasetId) {
      this.currentDatasetId = null;
      this.loadDataset$.next(this.cell.dataBinding.datasetId);
    }
  }

  private setupTableColumns(schemas: DataTableSchema[]): void {
    this.datasetSchemas = schemas;

    this.columns = schemas
      .filter(schema => !schema.isHidden)
      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0))
      .map(schema => ({
        fieldId: schema.fieldId,
        fieldName: schema.fieldName,
        displayName: schema.displayName || schema.fieldName,
        dataType: schema.dataType.toString()
      }));

    // Set displayed columns from configuration
    const configuredDisplayColumns = this.getConfigValue('displayColumns', []) as string[];
    if (configuredDisplayColumns && configuredDisplayColumns.length > 0) {
      this.displayedColumns = configuredDisplayColumns;
    } else {
      this.displayedColumns = this.columns.map(c => c.fieldName);
    }
  }

  private initPresetFilters(): void {
    const presetFields = this.getConfigValue('presetFilterFields', []) as string[];
    if (!presetFields || presetFields.length === 0 || this.columns.length === 0) return;

    const existingPresets = this.filterRows.filter(r => r.isPreset).map(r => r.fieldName);

    const newPresetRows: FilterRow[] = [];
    presetFields.forEach(fieldName => {
      if (existingPresets.includes(fieldName)) return;
      const schema = this.datasetSchemas.find(s => s.fieldName === fieldName);
      if (schema) {
        const dataType = schema.dataType?.toString() || DataSchemaDataType.String;
        const configuredMatchMode = this.filterDisplayMode === 'inline'
          ? this.getConfigValue('inlineFilterMatchMode', '') as string
          : '';
        newPresetRows.push({
          fieldId: schema.fieldId || null,
          fieldName: schema.fieldName,
          value: '',
          value2: '',
          dateValue: null,
          dateValue2: null,
          matchMode: (configuredMatchMode || (this.isNumericOrDateType(dataType) ? 'exact' : 'partial')) as MatchMode,
          dataType: dataType,
          availableMatchModes: this.getAvailableMatchModes(dataType),
          isPreset: true
        });
      }
    });
    // Prepend in configured order
    this.filterRows.unshift(...newPresetRows);
  }

  private restoreFilterState(): void {
    const saved = this.cell?.configuration?._activeFilterState;
    if (!saved || !Array.isArray(saved) || saved.length === 0) return;

    this.filterRows = saved.map((r: any) => ({
      ...r,
      availableMatchModes: r.dataType ? this.getAvailableMatchModes(r.dataType) : []
    }));
    this.hasActiveFilters = this.cell.configuration._activeFilterHasActive || false;
  }

  private loadTableData(datasetId: number, offset?: number): void {
    // Reset to first page when loading without explicit offset (e.g., filter change, initial load)
    if (offset === undefined) {
      this.currentPage = 0;
    }

    // Check if any configured dynamic filters failed to resolve — if so, show empty rather than unfiltered data
    if (this.cellFilterService.hasUnresolvedFilters(this.cell?.dataBinding?.cellFilters, this.viewManager)) {
      this.tableData = [];
      this.filteredData = [];
      this.selectedRowIndices.clear();
      this.selectAllChecked = false;
      this.isLoadingData = false;
      return;
    }

    this.isLoadingData = true;

    // Build cell-level filters
    const cellFilters = this.cellFilterService.buildCellFilters(this.cell?.dataBinding?.cellFilters, this.viewManager);

    // Build user-applied filters
    const userFilters = this.buildUserFilters();

    // Merge filters
    const filters = this.mergeFilters(cellFilters, userFilters);

    const limit = 500;
    this.pageSize = limit;
    const distinct = this.getConfigValue('distinct', false) as boolean;
    const orderByConfig = this.getConfigValue('orderBy', []) as any[];
    const orderBy: SortCriteria[] = orderByConfig.map(s => ({ fieldId: s.fieldId, direction: s.direction }));
    this.dynamicTableService.selectEntries(datasetId, limit, offset, filters, distinct, orderBy)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.tableData = this.stringifyRowData(response.rowData || []);
          this.filteredData = [...this.tableData];
          this.applySorting();

          // Update pagination state
          this.totalRecords = response.totalCount || response.rowData.length;
          this.paginationEnabled = this.totalRecords > limit;

          // Clear selection when data reloads
          this.selectedRowIndices.clear();
          this.selectAllChecked = false;

          // Auto-select first row if configured and data is present
          const autoSelectFirst = this.getConfigValue('autoSelectFirst', false) as boolean;
          if (autoSelectFirst && this.filteredData.length > 0) {
            this.selectedRowIndices.add(0);
          }

          this.emitSelectionChange();

          // Store all rows in context if configured
          this.emitAllRowsToContext();

          this.isLoadingData = false;
        },
        error: (error) => {
          console.error('Error loading table data:', error);
          this.tableData = [];
          this.filteredData = [];
          this.isLoadingData = false;
        }
      });
  }

  onPageChange(page: number): void {
    if (!this.currentDatasetId) return;
    this.currentPage = page;
    const offset = page * this.pageSize;
    this.selectedRowIndices.clear();
    this.selectAllChecked = false;
    this.loadTableData(this.currentDatasetId, offset);
  }

  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  private stringifyRowData(rowData: any[]): any[] {
    return rowData.map(row => {
      const stringifiedRow: any = {};
      for (const key in row) {
        if (row.hasOwnProperty(key)) {
          stringifiedRow[key] = row[key] === null || row[key] === undefined ? '' : row[key];
        }
      }
      return stringifiedRow;
    });
  }

  /**
   * Split a filter input on commas so users can filter by multiple values per field.
   * e.g. "2466, 2467" → ["2466", "2467"]. No commas → one-element array.
   */
  private splitMultiValueInput(raw: string | null | undefined): string[] {
    if (!raw) return [];
    return raw.split(',').map(v => v.trim()).filter(v => v.length > 0);
  }

  private buildUserFilters(): DynamicTableFilters | undefined {
    const filters: DynamicTableFilters = {};

    this.filterRows.forEach(filterRow => {
      if (!filterRow.fieldId) return;

      const hasValue = this.isDateType(filterRow.dataType)
        ? filterRow.dateValue != null
        : filterRow.value?.trim();

      if (!hasValue) return;

      let values: (string | number)[] = [];

      if (filterRow.matchMode === 'between') {
        if (this.isDateType(filterRow.dataType)) {
          if (filterRow.dateValue && filterRow.dateValue2) {
            values = [this.formatDateForFilter(filterRow.dateValue), this.formatDateForFilter(filterRow.dateValue2)];
          }
        } else {
          if (filterRow.value?.trim() && filterRow.value2?.trim()) {
            values = [filterRow.value.trim(), filterRow.value2.trim()];
          }
        }
      } else {
        if (this.isDateType(filterRow.dataType) && filterRow.dateValue) {
          // Date inputs come from a picker — no comma-split, single value
          values = [this.formatDateForFilter(filterRow.dateValue)];
        } else if (this.isNumericType(filterRow.dataType)) {
          values = this.splitMultiValueInput(filterRow.value)
            .map(v => parseFloat(v))
            .filter(n => !isNaN(n));
        } else {
          values = this.splitMultiValueInput(filterRow.value);
        }
      }

      if (values.length > 0) {
        filters[filterRow.fieldId] = {
          values: values,
          matchMode: filterRow.matchMode
        };
      }
    });

    return Object.keys(filters).length > 0 ? filters : undefined;
  }

  private formatDateForFilter(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00`;
  }

  private mergeFilters(cellFilters: DynamicTableFilters | undefined, userFilters: DynamicTableFilters | undefined): DynamicTableFilters | undefined {
    if (!cellFilters && !userFilters) return undefined;
    if (!cellFilters) return userFilters;
    if (!userFilters) return cellFilters;
    return { ...cellFilters, ...userFilters };
  }

  // Selection methods
  isRowSelected(rowIndex: number): boolean {
    return this.selectedRowIndices.has(rowIndex);
  }

  onCheckboxChange(row: TableData, rowIndex: number, isChecked: boolean): void {
    if (isChecked) {
      this.selectedRowIndices.add(rowIndex);
    } else {
      this.selectedRowIndices.delete(rowIndex);
    }

    this.updateSelectAllState();
    this.emitSelectionChange();
  }

  onSelectAllChange(isChecked: boolean): void {
    this.selectAllChecked = isChecked;

    if (isChecked) {
      this.filteredData.forEach((_, index) => {
        this.selectedRowIndices.add(index);
      });
    } else {
      this.selectedRowIndices.clear();
    }

    this.emitSelectionChange();
  }

  private updateSelectAllState(): void {
    if (this.filteredData.length === 0) {
      this.selectAllChecked = false;
    } else {
      this.selectAllChecked = this.selectedRowIndices.size === this.filteredData.length;
    }
  }

  private emitSelectionChange(): void {
    const contextVariable = this.getConfigValue('multiSelectContextVariable', '');
    if (!contextVariable || !this.viewManager) return;

    const fieldsToStore = this.getConfigValue('multiSelectFieldsToStore', []) as string[];
    const selectedItems: any[] = [];
    const selectedRows: TableData[] = [];

    // Build selected items array and capture every selected row. The full rows feed
    // additional contexts, which filter on ANY of the selected values — so selecting
    // two businesses pulls the inventory for both, not just the first.
    const sortedIndices = Array.from(this.selectedRowIndices).sort((a, b) => a - b);
    sortedIndices.forEach(rowIndex => {
      if (rowIndex < this.filteredData.length) {
        const row = this.filteredData[rowIndex];
        const itemToStore = this.extractFieldsFromRow(row, fieldsToStore);
        selectedItems.push(itemToStore);
        selectedRows.push(row);
      }
    });

    // Update context
    if (!this.viewManager.contextData) {
      this.viewManager.contextData = {};
    }
    this.viewManager.contextData[contextVariable] = selectedItems;

    const contextObjects: {[contextKey: string]: any} = {
      [contextVariable]: selectedItems
    };

    // Handle additional contexts (load related data based on the selected rows)
    const additionalContexts = this.getConfigValue('additionalContexts', []) as ContextObjectConfig[];
    if (additionalContexts && additionalContexts.length > 0) {
      if (selectedRows.length > 0) {
        // Load additional contexts using every selected row as source
        this.loadAdditionalContexts(selectedRows, contextObjects);
      } else {
        // Clear additional contexts when no selection
        this.clearAdditionalContexts(contextObjects);
      }
    } else {
      // No additional contexts, just emit the selection
      this.contextDataChange.emit(contextObjects);
    }
  }

  private loadAdditionalContexts(sourceRows: TableData[], existingContexts: {[contextKey: string]: any}): void {
    const additionalContexts = this.getConfigValue('additionalContexts', []) as ContextObjectConfig[];

    additionalContexts.forEach(additionalConfig => {
      this.loadSingleAdditionalContext(sourceRows, additionalConfig, existingContexts);
    });
  }

  private loadSingleAdditionalContext(
    sourceRows: TableData[],
    config: ContextObjectConfig,
    existingContexts: {[contextKey: string]: any}
  ): void {
    if (!config.contextVariable || !config.datasetId) {
      return;
    }

    // A Single Record context fetches with limit 1, so widening its filter to
    // `IN (row1, row2)` would return an arbitrary row rather than more data.
    // Those stay pinned to the first selected row; List contexts match all rows.
    const isSingleRecord = config.loadFirstMatch !== false;
    const filterRows = isSingleRecord ? sourceRows.slice(0, 1) : sourceRows;

    // Build filters for the additional context
    // Pass the selected row(s) for 'firstSelectedRow' filter type
    const cellFilters = this.cellFilterService.buildCellFilters(
      config.cellFilters,
      this.viewManager,
      filterRows,
      { multiValue: !isSingleRecord }
    );

    // Load data with cell filters
    const limit = isSingleRecord ? 1 : 500;

    this.dynamicTableService.selectEntries(config.datasetId, limit, undefined, cellFilters || undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.handleAdditionalContextResponse(response, config, existingContexts),
        error: (error) => this.handleAdditionalContextError(error, config, existingContexts)
      });
  }

  private handleAdditionalContextResponse(
    response: any,
    config: ContextObjectConfig,
    existingContexts: {[contextKey: string]: any}
  ): void {
    const updatedContexts = { ...existingContexts };

    if (response.rowData && response.rowData.length > 0) {
      if (config.loadFirstMatch !== false) {
        // Store the first matching record
        updatedContexts[config.contextVariable] = {
          ...response.rowData[0],
          __metadata: {
            datasetId: config.datasetId,
            isAdditional: true,
            source: 'multiSelectFirstRow'
          }
        };
      } else {
        // Store all matching records as a bare array — the standard list-context shape.
        updatedContexts[config.contextVariable] = response.rowData;
      }
    } else {
      // No matching records found — empty list, or an empty object preserving field structure
      updatedContexts[config.contextVariable] = this.createEmptyContextFromExisting(
        config.contextVariable, config.datasetId, config.loadFirstMatch === false
      );
    }

    // Update viewManager context
    if (this.viewManager?.contextData) {
      this.viewManager.contextData[config.contextVariable] = updatedContexts[config.contextVariable];
    }

    this.contextDataChange.emit(updatedContexts);
  }

  private handleAdditionalContextError(
    error: any,
    config: ContextObjectConfig,
    existingContexts: {[contextKey: string]: any}
  ): void {
    console.error(`Error loading additional context from dataset ${config.datasetId}:`, error);
    const updatedContexts = { ...existingContexts };
    const emptyObj = this.createEmptyContextFromExisting(
      config.contextVariable, config.datasetId, config.loadFirstMatch === false
    );
    updatedContexts[config.contextVariable] = emptyObj;

    // Update viewManager context
    if (this.viewManager?.contextData) {
      this.viewManager.contextData[config.contextVariable] = emptyObj;
    }

    this.contextDataChange.emit(updatedContexts);
  }

  private emitAllRowsToContext(): void {
    const storeAllRows = this.getConfigValue('storeAllRowsInContext', false);
    const contextVariable = this.getConfigValue('allRowsContextVariable', '');
    if (!storeAllRows || !contextVariable) return;

    this.contextDataChange.emit({ [contextVariable]: [...this.filteredData] });
  }

  private clearAdditionalContexts(existingContexts: {[contextKey: string]: any}): void {
    const additionalContexts = this.getConfigValue('additionalContexts', []) as ContextObjectConfig[];
    const updatedContexts = { ...existingContexts };

    additionalContexts.forEach(additionalConfig => {
      if (additionalConfig.contextVariable && additionalConfig.contextVariable.trim()) {
        const emptyObj = this.createEmptyContextFromExisting(
          additionalConfig.contextVariable, additionalConfig.datasetId, additionalConfig.loadFirstMatch === false
        );
        updatedContexts[additionalConfig.contextVariable] = emptyObj;

        // Update viewManager context
        if (this.viewManager?.contextData) {
          this.viewManager.contextData[additionalConfig.contextVariable] = emptyObj;
        }
      }
    });

    this.contextDataChange.emit(updatedContexts);
  }

  private createEmptyContextObject(): any {
    if (this.columns && this.columns.length > 0) {
      const emptyObject: any = {};
      this.columns.forEach(column => {
        emptyObject[column.fieldName] = null;
      });
      // Mark as empty so action buttons know to add, not update
      emptyObject.__metadata = {
        datasetId: this.cell?.dataBinding?.datasetId || null,
        isEmptyContext: true
      };
      return emptyObject;
    }
    return {};
  }

  /**
   * Create an empty context object by looking at the current context's keys.
   * Used for additional contexts where the schema comes from a different dataset.
   */
  /**
   * Empty value for a context. List contexts get a bare `[]` — the standard list
   * shape — so downstream cells keep seeing a list instead of losing their
   * list-ness the moment a selection matches nothing.
   */
  private createEmptyContextFromExisting(contextVariable: string, datasetId?: number, isList: boolean = false): any {
    if (isList) {
      return [];
    }

    const existing = this.viewManager?.contextData?.[contextVariable];
    if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
      const emptyObject: any = {};
      for (const key of Object.keys(existing)) {
        if (key === '__metadata') {
          emptyObject.__metadata = { ...existing.__metadata, isEmptyContext: true, isNewRecord: false };
        } else {
          emptyObject[key] = null;
        }
      }
      return emptyObject;
    }
    // No existing context to copy from — return a minimal empty context object
    return {
      __metadata: {
        datasetId: datasetId || null,
        isEmptyContext: true,
        isNewRecord: false
      }
    };
  }

  private extractFieldsFromRow(row: TableData, fieldsToStore: string[]): any {
    // If no fields specified, store entire row
    if (!fieldsToStore || fieldsToStore.length === 0) {
      return { ...row };
    }

    // Store only specified fields
    const extracted: any = {};
    fieldsToStore.forEach(fieldName => {
      if (row.hasOwnProperty(fieldName)) {
        extracted[fieldName] = row[fieldName];
      }
    });
    return extracted;
  }

  get selectedCount(): number {
    return this.selectedRowIndices.size;
  }

  // Display helpers
  getDisplayedColumns(): TableColumn[] {
    // Return columns in the configured display order
    return this.displayedColumns
      .map(name => this.columns.find(col => col.fieldName === name))
      .filter((col): col is TableColumn => !!col);
  }

  formatDate(value: any): string {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString();
  }

  formatNumber(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    return String(value);
  }

  onSortColumn(column: TableColumn): void {
    if (this.sortColumn === column.fieldName) {
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        this.sortColumn = null;
        this.sortDirection = null;
      }
    } else {
      this.sortColumn = column.fieldName;
      this.sortDirection = 'asc';
    }
    this.applySorting();
    // Clear selections since row indices change with sort
    this.selectedRowIndices.clear();
    this.selectAllChecked = false;
    this.emitSelectionChange();
  }

  private applySorting(): void {
    if (!this.sortColumn || !this.sortDirection) {
      this.filteredData = [...this.tableData];
      return;
    }
    const field = this.sortColumn;
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    this.filteredData.sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;
      if (typeof valA === 'number' && typeof valB === 'number') {
        return (valA - valB) * dir;
      }
      return String(valA).localeCompare(String(valB)) * dir;
    });
  }

  trackByColumn(index: number, column: TableColumn): string {
    return column.fieldName;
  }

  // Navigation
  onNavigate(): void {
    if (this.selectedCount === 0) return;

    const navigationTabId = this.getConfigValue('navigationTabId', '');
    if (navigationTabId) {
      this.tabNavigation.emit(navigationTabId);
    }
  }

  // Filter methods
  openFilterDialog(): void {
    this.dialog.open(this.filterDialog, {
      width: '600px',
      maxHeight: '80vh',
      panelClass: 'filter-dialog'
    });
  }

  addFilterRow(): void {
    this.filterRows.push({
      fieldId: null,
      fieldName: '',
      value: '',
      matchMode: 'partial',
      dataType: '',
      availableMatchModes: []
    });
  }

  removeFilterRow(index: number): void {
    this.filterRows.splice(index, 1);
  }

  onFilterFieldChange(filterRow: FilterRow): void {
    const column = this.columns.find(c => c.fieldName === filterRow.fieldName);
    if (column) {
      filterRow.fieldId = column.fieldId;
      filterRow.dataType = column.dataType;
      filterRow.availableMatchModes = this.getAvailableMatchModes(column.dataType);

      if (this.isNumericOrDateType(column.dataType)) {
        filterRow.matchMode = 'exact';
      } else {
        filterRow.matchMode = 'partial';
      }

      filterRow.value = '';
      filterRow.value2 = '';
      filterRow.dateValue = null;
      filterRow.dateValue2 = null;
    }
  }

  onMatchModeChange(filterRow: FilterRow): void {
    // Reset values when match mode changes
    if (filterRow.matchMode !== 'between') {
      filterRow.value2 = '';
      filterRow.dateValue2 = null;
    }
  }

  onFilterDateChange(filterRow: FilterRow, isSecondValue: boolean): void {
    if (isSecondValue) {
      if (filterRow.dateValue2) {
        filterRow.value2 = this.formatDateForFilter(filterRow.dateValue2);
      } else {
        filterRow.value2 = '';
      }
    } else {
      if (filterRow.dateValue) {
        filterRow.value = this.formatDateForFilter(filterRow.dateValue);
      } else {
        filterRow.value = '';
      }
    }
  }

  getAvailableFilterColumns(): TableColumn[] {
    const configuredFilterColumns: string[] = this.getConfigValue('filterColumns', []);
    if (configuredFilterColumns && configuredFilterColumns.length > 0) {
      const result = configuredFilterColumns
        .map(name => this.columns.find(col => col.fieldName === name))
        .filter((col): col is TableColumn => !!col);

      const presetFields: string[] = this.getConfigValue('presetFilterFields', []);
      const resultNames = new Set(result.map(col => col.fieldName));
      for (const presetName of presetFields) {
        if (!resultNames.has(presetName)) {
          const col = this.columns.find(c => c.fieldName === presetName);
          if (col) result.push(col);
        }
      }

      return result;
    }
    return this.columns;
  }

  getRowBackgroundColor(rowIndex: number): string | null {
    if (!this.getConfigValue('alternateRowColors', false)) return null;
    const evenColor = this.getConfigValue('rowEvenColor', '#f0f4f8');
    const oddColor = this.getConfigValue('rowOddColor', '#ffffff');
    return rowIndex % 2 === 0 ? oddColor : evenColor;
  }

  getMatchModeLabel(matchMode: string): string {
    const labels: { [key: string]: string } = {
      'exact': 'Equals', 'not_equal': 'Not Equal', 'partial': 'Contains',
      'starts_with': 'Starts With', 'greater_than': 'Greater Than',
      'less_than': 'Less Than', 'greater_than_or_equal': 'Greater Than or Equal',
      'less_than_or_equal': 'Less Than or Equal', 'between': 'Between'
    };
    return labels[matchMode] || matchMode;
  }

  getMatchModeAbbrev(matchMode: string): string {
    const abbrevs: { [key: string]: string } = {
      'exact': '=', 'not_equal': '≠', 'partial': '∋',
      'starts_with': 'A…', 'greater_than': '>',
      'less_than': '<', 'greater_than_or_equal': '≥',
      'less_than_or_equal': '≤', 'between': '⟷'
    };
    return abbrevs[matchMode] || '=';
  }

  onInlineMatchModeChange(filterRow: any, matchMode: MatchMode): void {
    filterRow.matchMode = matchMode;
    this.applyInlineFilters();
  }

  getAvailableMatchModes(dataType: string): { label: string; value: MatchMode }[] {
    const stringModes = [
      { label: 'Starts with', value: 'starts_with' as MatchMode },
      { label: 'Contains', value: 'partial' as MatchMode },
      { label: 'Equals', value: 'exact' as MatchMode },
      { label: 'Not Equal', value: 'not_equal' as MatchMode }
    ];

    const numericModes = [
      { label: 'Equals', value: 'exact' as MatchMode },
      { label: 'Not Equal', value: 'not_equal' as MatchMode },
      { label: 'Greater than', value: 'greater_than' as MatchMode },
      { label: 'Less than', value: 'less_than' as MatchMode },
      { label: 'Between', value: 'between' as MatchMode }
    ];

    if (this.isNumericOrDateType(dataType)) {
      return numericModes;
    }
    return stringModes;
  }

  isNumericOrDateType(dataType: string): boolean {
    const numericTypes = [DataSchemaDataType.Integer, DataSchemaDataType.Decimal, DataSchemaDataType.DateTime];
    return numericTypes.includes(dataType as DataSchemaDataType);
  }

  isNumericType(dataType: string): boolean {
    const numericTypes = [DataSchemaDataType.Integer, DataSchemaDataType.Decimal];
    return numericTypes.includes(dataType as DataSchemaDataType);
  }

  isDateType(dataType: string): boolean {
    return dataType === DataSchemaDataType.DateTime;
  }

  isDateTimeFilterRow(filterRow: FilterRow): boolean {
    return filterRow.dataType === DataSchemaDataType.DateTime;
  }

  applyFilters(): void {
    this.hasActiveFilters = this.filterRows.some(row => {
      if (!row.fieldId) return false;
      if (this.isDateType(row.dataType)) {
        return row.dateValue != null;
      }
      return row.value?.trim();
    });

    // Clear selection when filters change
    this.selectedRowIndices.clear();
    this.selectAllChecked = false;

    if (this.cell?.dataBinding?.datasetId) {
      this.loadTableData(this.cell.dataBinding.datasetId);
    }
  }

  clearFilters(): void {
    this.filterRows = [];
    this.hasActiveFilters = false;
    this.selectedRowIndices.clear();
    this.selectAllChecked = false;

    if (this.cell?.dataBinding?.datasetId) {
      this.loadTableData(this.cell.dataBinding.datasetId);
    }
  }

  // Inline filter methods
  addInlineFilter(): void {
    this.filterRows.push({
      fieldId: null,
      fieldName: '',
      value: '',
      value2: '',
      dateValue: null,
      dateValue2: null,
      matchMode: 'exact',
      dataType: '',
      availableMatchModes: []
    });
  }

  removeInlineFilter(index: number): void {
    this.filterRows.splice(index, 1);
    this.applyInlineFilters();
  }

  onInlineFilterFieldChange(filterRow: FilterRow): void {
    const column = this.columns.find(c => c.fieldName === filterRow.fieldName);
    if (column) {
      filterRow.fieldId = column.fieldId;
      filterRow.dataType = column.dataType;
      filterRow.availableMatchModes = this.getAvailableMatchModes(column.dataType);
      const configuredMatchMode = this.getConfigValue('inlineFilterMatchMode', '') as string;
      filterRow.matchMode = (configuredMatchMode || (this.isNumericOrDateType(column.dataType) ? 'exact' : 'partial')) as MatchMode;
      filterRow.value = '';
      filterRow.value2 = '';
      filterRow.dateValue = null;
      filterRow.dateValue2 = null;
    }
  }

  onInlineFilterValueChange(): void {
    this.inlineFilterApply$.next();
  }

  onInlineFilterDateChange(filterRow: FilterRow): void {
    if (filterRow.dateValue) {
      filterRow.value = this.formatDateForFilter(filterRow.dateValue);
    } else {
      filterRow.value = '';
    }
    this.applyInlineFilters();
  }

  applyInlineFilters(): void {
    this.hasActiveFilters = this.filterRows.some(row => {
      if (!row.fieldId) return false;
      if (this.isDateType(row.dataType)) {
        return row.dateValue != null;
      }
      return row.value?.trim();
    });

    // Clear selection when filters change
    this.selectedRowIndices.clear();
    this.selectAllChecked = false;

    if (this.currentDatasetId) {
      this.loadTableData(this.currentDatasetId);
    }
  }

  clearInlineFilters(): void {
    this.filterRows = [];
    this.hasActiveFilters = false;
    this.selectedRowIndices.clear();
    this.selectAllChecked = false;

    if (this.currentDatasetId) {
      this.loadTableData(this.currentDatasetId);
    }
  }

  refreshTableData(): void {
    if (this.currentDatasetId) {
      this.loadTableData(this.currentDatasetId);
    }
  }
}
